import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ThumbsUp,
  Heart,
  Lightbulb,
  Brain,
  Flame,
  Share2,
  Copy,
  Check,
  MessageCircle,
  Send,
  Trash2,
} from "lucide-react";
import {
  getStorySocial,
  getMyReaction,
  setMyReaction,
  addComment,
  deleteMyComment,
  type Reaction,
} from "@/lib/api/story-social.functions";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const REACTIONS: Array<{
  key: Reaction;
  label: string;
  Icon: typeof ThumbsUp;
  tone: string;
}> = [
  { key: "like", label: "Like", Icon: ThumbsUp, tone: "text-sky-600" },
  { key: "love", label: "Love", Icon: Heart, tone: "text-rose-600" },
  { key: "insightful", label: "Insightful", Icon: Lightbulb, tone: "text-amber-600" },
  { key: "thinking", label: "Hmm", Icon: Brain, tone: "text-indigo-600" },
  { key: "fire", label: "Fire", Icon: Flame, tone: "text-orange-600" },
];

export function StorySocial({
  storyId,
  shareText,
  shareUrl,
}: {
  storyId: string;
  shareText: string;
  shareUrl: string;
}) {
  const qc = useQueryClient();
  const [userId, setUserId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [draft, setDraft] = useState("");
  const [openComments, setOpenComments] = useState(false);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getUser().then(({ data }) => {
      if (mounted) setUserId(data.user?.id ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setUserId(session?.user?.id ?? null);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const fetchSocial = useServerFn(getStorySocial);
  const fetchMine = useServerFn(getMyReaction);
  const mutateReaction = useServerFn(setMyReaction);
  const sendComment = useServerFn(addComment);
  const removeComment = useServerFn(deleteMyComment);

  const socialQ = useQuery({
    queryKey: ["story-social", storyId],
    queryFn: () => fetchSocial({ data: { storyId } }),
    staleTime: 1000 * 30,
  });

  const mineQ = useQuery({
    queryKey: ["story-mine", storyId, userId],
    queryFn: () => fetchMine({ data: { storyId } }),
    enabled: !!userId,
  });

  // Realtime: invalidate on new reactions/comments for this story
  useEffect(() => {
    const channel = supabase
      .channel(`story-social-${storyId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "story_reactions", filter: `story_id=eq.${storyId}` },
        () => qc.invalidateQueries({ queryKey: ["story-social", storyId] }),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "story_comments", filter: `story_id=eq.${storyId}` },
        () => qc.invalidateQueries({ queryKey: ["story-social", storyId] }),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [storyId, qc]);

  const reactMut = useMutation({
    mutationFn: (next: Reaction | null) =>
      mutateReaction({ data: { storyId, reaction: next } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["story-social", storyId] });
      qc.invalidateQueries({ queryKey: ["story-mine", storyId, userId] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not save your reaction"),
  });

  const commentMut = useMutation({
    mutationFn: (body: string) => sendComment({ data: { storyId, body } }),
    onSuccess: () => {
      setDraft("");
      qc.invalidateQueries({ queryKey: ["story-social", storyId] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not post comment"),
  });

  const deleteMut = useMutation({
    mutationFn: (commentId: string) => removeComment({ data: { commentId } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["story-social", storyId] }),
    onError: (e: any) => toast.error(e?.message ?? "Could not delete comment"),
  });

  const counts = socialQ.data?.counts;
  const total = socialQ.data?.total ?? 0;
  const comments = socialQ.data?.comments ?? [];
  const mine = mineQ.data?.reaction ?? null;

  function clickReaction(k: Reaction) {
    if (!userId) {
      toast.message("Sign in to react", {
        description: "Create a free account to like, react and comment.",
      });
      return;
    }
    reactMut.mutate(mine === k ? null : k);
  }

  async function handleShare() {
    const text = `${shareText} ${shareUrl}`;
    try {
      if (typeof navigator !== "undefined" && (navigator as any).share) {
        await (navigator as any).share({
          title: "Today's legal story",
          text: shareText,
          url: shareUrl,
        });
        return;
      }
    } catch {
      /* fall through to copy */
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  }

  function whatsappHref() {
    return `https://wa.me/?text=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`;
  }

  return (
    <div className="mt-5 space-y-4 border-t border-border pt-4">
      {/* Reactions row */}
      <div className="flex flex-wrap items-center gap-2">
        {REACTIONS.map(({ key, label, Icon, tone }) => {
          const active = mine === key;
          const count = counts?.[key] ?? 0;
          return (
            <button
              key={key}
              type="button"
              onClick={() => clickReaction(key)}
              aria-pressed={active}
              aria-label={`${label} (${count})`}
              disabled={reactMut.isPending}
              className={
                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition " +
                (active
                  ? "border-navy bg-navy text-navy-foreground"
                  : "border-input bg-background text-foreground/80 hover:border-navy")
              }
            >
              <Icon className={`h-3.5 w-3.5 ${active ? "" : tone}`} />
              <span>{label}</span>
              {count > 0 && (
                <span
                  className={
                    "rounded-full px-1.5 text-[10px] " +
                    (active
                      ? "bg-navy-foreground/20"
                      : "bg-muted text-muted-foreground")
                  }
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
        <span className="ml-auto text-xs text-muted-foreground">
          {total} {total === 1 ? "reaction" : "reactions"}
        </span>
      </div>

      {/* Action row */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setOpenComments((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium text-navy hover:border-navy"
        >
          <MessageCircle className="h-3.5 w-3.5" />
          {comments.length} {comments.length === 1 ? "comment" : "comments"}
        </button>
        <button
          type="button"
          onClick={handleShare}
          className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium text-navy hover:border-navy"
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-emerald-600" />
          ) : (
            <Share2 className="h-3.5 w-3.5" />
          )}
          Share
        </button>
        <a
          href={whatsappHref()}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium text-emerald-700 hover:border-emerald-600"
        >
          WhatsApp
        </a>
        <button
          type="button"
          onClick={async () => {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            toast.success("Link copied");
            setTimeout(() => setCopied(false), 2000);
          }}
          className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium text-navy hover:border-navy"
        >
          <Copy className="h-3.5 w-3.5" /> Copy link
        </button>
      </div>

      {openComments && (
        <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-4">
          {userId ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const body = draft.trim();
                if (body.length < 2) return;
                commentMut.mutate(body);
              }}
              className="flex gap-2"
            >
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                maxLength={2000}
                placeholder="Share your thoughts…"
                className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
              />
              <button
                type="submit"
                disabled={commentMut.isPending || draft.trim().length < 2}
                className="inline-flex items-center gap-1 rounded-md bg-navy px-3 py-2 text-sm font-medium text-navy-foreground hover:bg-navy/90 disabled:opacity-50"
              >
                <Send className="h-3.5 w-3.5" /> Post
              </button>
            </form>
          ) : (
            <p className="text-xs text-muted-foreground">
              <Link to="/auth" className="font-medium text-navy hover:underline">
                Sign in
              </Link>{" "}
              to add a comment.
            </p>
          )}

          {comments.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Be the first to comment.
            </p>
          ) : (
            <ul className="space-y-3">
              {comments.map((c) => (
                <li
                  key={c.id}
                  className="rounded-md border border-border bg-background p-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-navy">
                      {c.author_name || "Member"}
                    </p>
                    <div className="flex items-center gap-2">
                      <time className="text-[10px] text-muted-foreground">
                        {new Date(c.created_at).toLocaleString("en-ZA")}
                      </time>
                      {c.mine && (
                        <button
                          type="button"
                          onClick={() => deleteMut.mutate(c.id)}
                          aria-label="Delete comment"
                          className="text-muted-foreground hover:text-rose-600"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-foreground/90">
                    {c.body}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
